import useAuth from "./hooks/useAuth";
import { useEffect, useMemo, useState } from "react";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";
import Paginator from "../library/components/Paginator";
import Selector from "./Components/Selector";
import CheckUserStats from "./CheckUserStats";
import Select, { components } from "react-select";

/** =========================
 *  Languages (same list as Register)
 *  ========================= */

const languageOptionsRaw = [
  { value: "en", label: "English", flag: "🇬🇧" },
  { value: "en-US", label: "English (US)", flag: "🇺🇸" },
  { value: "no", label: "Norwegian", flag: "🇳🇴" },
  { value: "sv", label: "Swedish", flag: "🇸🇪" },
  { value: "da", label: "Danish", flag: "🇩🇰" },
  { value: "fi", label: "Finnish", flag: "🇫🇮" },
  { value: "is", label: "Icelandic", flag: "🇮🇸" },

  { value: "de", label: "German", flag: "🇩🇪" },
  { value: "nl", label: "Dutch", flag: "🇳🇱" },
  { value: "fr", label: "French", flag: "🇫🇷" },
  { value: "es", label: "Spanish", flag: "🇪🇸" },
  { value: "it", label: "Italian", flag: "🇮🇹" },
  { value: "pt", label: "Portuguese", flag: "🇵🇹" },
  { value: "pt-BR", label: "Portuguese (Brazil)", flag: "🇧🇷" },
  { value: "ro", label: "Romanian", flag: "🇷🇴" },
  { value: "el", label: "Greek", flag: "🇬🇷" },
  { value: "hu", label: "Hungarian", flag: "🇭🇺" },
  { value: "cs", label: "Czech", flag: "🇨🇿" },
  { value: "sk", label: "Slovak", flag: "🇸🇰" },
  { value: "pl", label: "Polish", flag: "🇵🇱" },
  { value: "uk", label: "Ukrainian", flag: "🇺🇦" },
  { value: "ru", label: "Russian", flag: "🇷🇺" },
  { value: "bg", label: "Bulgarian", flag: "🇧🇬" },
  { value: "sr", label: "Serbian", flag: "🇷🇸" },
  { value: "hr", label: "Croatian", flag: "🇭🇷" },

  { value: "tr", label: "Turkish", flag: "🇹🇷" },
  { value: "ar", label: "Arabic", flag: "🇸🇦" },
  { value: "he", label: "Hebrew", flag: "🇮🇱" },
  { value: "fa", label: "Persian (Farsi)", flag: "🇮🇷" },

  { value: "hi", label: "Hindi", flag: "🇮🇳" },
  { value: "bn", label: "Bengali", flag: "🇧🇩" },
  { value: "ur", label: "Urdu", flag: "🇵🇰" },

  { value: "zh", label: "Chinese (Mandarin)", flag: "🇨🇳" },
  { value: "yue", label: "Chinese (Cantonese)", flag: "🇭🇰" },
  { value: "ja", label: "Japanese", flag: "🇯🇵" },
  { value: "ko", label: "Korean", flag: "🇰🇷" },
  { value: "th", label: "Thai", flag: "🇹🇭" },
  { value: "vi", label: "Vietnamese", flag: "🇻🇳" },
  { value: "id", label: "Indonesian", flag: "🇮🇩" },
  { value: "ms", label: "Malay", flag: "🇲🇾" },

  { value: "sw", label: "Swahili", flag: "🇰🇪" },
];

const languageOptions = [...languageOptionsRaw].sort((a, b) => a.label.localeCompare(b.label));

const LanguageOption = (props) => (
  <components.Option {...props}>
    <span className="mr-2">{props.data.flag}</span>
    {props.data.label}
  </components.Option>
);

const LanguageSingleValue = (props) => (
  <components.SingleValue {...props}>
    <span className="mr-2">{props.data.flag}</span>
    {props.data.label}
  </components.SingleValue>
);

const LanguageMultiValueLabel = (props) => (
  <components.MultiValueLabel {...props}>
    <span className="mr-1">{props.data.flag}</span>
    {props.data.label}
  </components.MultiValueLabel>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: "1px solid #d1d5db",
    borderRadius: "0.75rem",
    minHeight: "42px",
    fontSize: "14px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.12)" : "none",
    borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
    "&:hover": { borderColor: state.isFocused ? "#3b82f6" : "#9ca3af" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "14px",
    backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "#eff6ff" : "white",
    color: state.isSelected ? "white" : "#374151",
    "&:active": { backgroundColor: "#3b82f6", color: "white" },
  }),
  multiValue: (base) => ({
    ...base,
    borderRadius: "9999px",
    border: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  }),
  multiValueRemove: (base) => ({
    ...base,
    borderRadius: "9999px",
    ":hover": { backgroundColor: "#fee2e2", color: "#991b1b" },
  }),
};

const makeOptionFromValue = (v) =>
  languageOptions.find((x) => x.value === v) || { value: v, label: v, flag: "🏳️" };

const cleanText = (s) => (typeof s === "string" ? s.trim() : s);

const dedupeStringsCaseInsensitive = (arr) => {
  const seen = new Set();
  const out = [];
  for (const x of arr || []) {
    const v = cleanText(x);
    if (!v) continue;
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
};

// zero-width strip (matches backend concept)
const stripZeroWidth = (s) =>
  typeof s === "string" ? s.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, "") : s;

// MUST match backend canonical_username():
// - strip zero-width
// - trim
// - remove ALL whitespace
// - lowercase
const canonicalUsername = (s) =>
  stripZeroWidth(String(s || ""))
    .trim()
    .replace(/\s+/g, "")
    .toLowerCase();

/** =========================
 *  AccessInterface
 *  ========================= */

const AccessInterface = ({ successHandler }) => {
  const { setAuth } = useAuth();

  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const [subscribed, setSubscribed] = useState(false);

  const [accessSearch, setAccessSearch] = useState("");
  const [requestSearch, setRequestSearch] = useState("");

  const [accessPage, setAccessPage] = useState(1);
  const [requestPage, setRequestPage] = useState(1);

  const [accessPages, setAccessPages] = useState(0);
  const [requestPages, setRequestPages] = useState(0);

  const [role, setRole] = useState({ label: "All", value: "All" });

  const [changeList, setChangeList] = useState([]);

  const tabs = useMemo(() => ["User management", "Stats"], []);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [checkUser, setCheckUser] = useState(null);

  // Language editor modal state
  const [editLangUser, setEditLangUser] = useState(null);
  const [editL1, setEditL1] = useState(null);
  const [editOther, setEditOther] = useState([]);
  const [langSaving, setLangSaving] = useState(false);
  const [langSaveMsg, setLangSaveMsg] = useState("");

  // Canonical modal state (NEW) – sets username_canon without renaming display username
  const [canonUser, setCanonUser] = useState(null); // user object
  const [canonSaving, setCanonSaving] = useState(false);
  const [canonMsg, setCanonMsg] = useState("");

  // Rename modal (still supported by backend, but optional now)
  const [renameUser, setRenameUser] = useState(null); // user object
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameMsg, setRenameMsg] = useState("");

  const options = useMemo(() => ["USER", "CREATOR", "ADMIN"], []);
  const searchOptions = useMemo(
    () => [
      { label: "All", value: "All" },
      { label: "USER", value: "USER" },
      { label: "CREATOR", value: "CREATOR" },
      { label: "ADMIN", value: "ADMIN" },
    ],
    []
  );

  /** ---------- Computed: duplicates + canon status (based on backend fields) ---------- */

  // Prefer backend username_canon if present; fallback to computed canonicalUsername(username)
  const getCanonKey = (u) => {
    const stored = u?.username_canon;
    if (typeof stored === "string" && stored.trim()) return stored.trim();
    return canonicalUsername(u?.username || "");
  };

  const canonCounts = useMemo(() => {
    const m = new Map();
    for (const u of users || []) {
      const c = getCanonKey(u);
      if (!c) continue;
      m.set(c, (m.get(c) || 0) + 1);
    }
    return m;
  }, [users]);

  const isCanonDuplicate = (u) => {
    const c = getCanonKey(u);
    return c && (canonCounts.get(c) || 0) > 1;
  };

  const hasCanonField = (u) => typeof u?.username_canon === "string" && u.username_canon.trim().length > 0;

  // Backend: username_canon should equal canonical_username(display)
  const canonMatchesBackend = (u) => {
    if (!hasCanonField(u)) return false;
    return u.username_canon === canonicalUsername(u.username);
  };

  const needsCanonSet = (u) => !hasCanonField(u);
  const needsCanonFix = (u) => hasCanonField(u) && !canonMatchesBackend(u);

  /** ---------- API helpers ---------- */

  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem("token");
    const res = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    if (res.status === 401) {
      localStorage.removeItem("token");
      setAuth({});
      return null;
    }
    return res;
  };

  const updateAccessList = async (page = 1) => {
    let query = `?page=${page}`;
    if (accessSearch !== "") query += `&search=${encodeURIComponent(accessSearch)}`;
    if (role.value !== "All") query += `&role=${encodeURIComponent(role.value)}`;

    const res = await authFetch(`${process.env.REACT_APP_USER_API_URL}/admin/access-list${query}`, {
      method: "GET",
    });
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (json) setUsers(Array.isArray(json) ? json : []);

    await getAccessPages();
  };

  const updateRequestList = async (page = 1) => {
    const q = `${process.env.REACT_APP_USER_API_URL}/admin/request-list?page=${page}${
      requestSearch !== "" ? `&search=${encodeURIComponent(requestSearch)}` : ""
    }`;

    const res = await authFetch(q, { method: "GET" });
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (json) setRequests(Array.isArray(json) ? json : []);

    await getRequestPages();
  };

  const getAccessPages = async () => {
    let query = `?`;

    if (accessSearch !== "") {
      query += `search=${encodeURIComponent(accessSearch)}`;
      if (role.value !== "All") query += `&`;
    }
    if (role.value !== "All") query += `role=${encodeURIComponent(role.value)}`;
    if (query === "?") query = "";

    const res = await authFetch(`${process.env.REACT_APP_USER_API_URL}/admin/access-pages${query}`, {
      method: "GET",
    });
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (json) setAccessPages(json["pages"] || 0);
  };

  const getRequestPages = async () => {
    const q = `${process.env.REACT_APP_USER_API_URL}/admin/request-pages${
      requestSearch !== "" ? `?search=${encodeURIComponent(requestSearch)}` : ""
    }`;

    const res = await authFetch(q, { method: "GET" });
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (json) setRequestPages(json["pages"] || 0);
  };

  const changeAccess = async (username, newRole) => {
    await authFetch(
      `${process.env.REACT_APP_USER_API_URL}/admin/change-access/${encodeURIComponent(username)}/${encodeURIComponent(
        newRole
      )}`,
      { method: "POST" }
    );
  };

  const denyAccess = async (username, newRole) => {
    await authFetch(
      `${process.env.REACT_APP_USER_API_URL}/admin/deny-request/${encodeURIComponent(username)}/${encodeURIComponent(
        newRole
      )}`,
      { method: "POST" }
    );
  };

  const deleteUser = async (username) => {
    await authFetch(`${process.env.REACT_APP_USER_API_URL}/admin/delete-user/${encodeURIComponent(username)}`, {
      method: "DELETE",
    });
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    const currentEmail = email;
    setEmail("");

    const res = await authFetch(
      `${process.env.REACT_APP_USER_API_URL}/admin/invite/${encodeURIComponent(currentEmail)}`,
      { method: "POST" }
    );
    if (!res) return;

    if (res.ok) setEmailError("Email sent successfully");
    if (res.status === 409) setEmailError("User with this email already exists");
  };

  const handleCheck = async () => {
    const newState = !subscribed;
    setSubscribed(newState);

    await authFetch(`${process.env.REACT_APP_USER_API_URL}/admin/subscribe/${newState}`, { method: "POST" });
  };

  const addChange = (change) => setChangeList((prev) => [...prev, change]);

  const addChangeAccess = (username, oldRole, newRole) => addChange({ username, action: `${oldRole}->${newRole}` });
  const addDenyAccess = (username, oldRole, newRole) => addChange({ username, action: `DENY ${oldRole}->${newRole}` });
  const addDeleteUser = (username) => addChange({ username, action: "DELETE" });

  const removeChange = (i) => {
    setChangeList((prev) => {
      const copy = [...prev];
      copy.splice(i, 1);
      return copy;
    });
  };

  const commit = async () => {
    for (const change of changeList) {
      if (change.action === "DELETE") await deleteUser(change.username);
      else if (change.action.split(" ")[0] === "DENY") await denyAccess(change.username, change.action.split("->")[1]);
      else await changeAccess(change.username, change.action.split("->")[1]);
    }
    setChangeList([]);
    updateAccessList(accessPage);
    updateRequestList(requestPage);
    getAccessPages();
    getRequestPages();
  };

  /** ---------- Canonical Modal (NEW) ---------- */

  const openCanonModal = (user) => {
    setCanonMsg("");
    setCanonUser(user);
  };

  const closeCanonModal = () => {
    setCanonUser(null);
    setCanonMsg("");
    setCanonSaving(false);
  };

  const saveCanonical = async () => {
    if (!canonUser?.id) return;

    setCanonSaving(true);
    setCanonMsg("");

    const res = await authFetch(
      `${process.env.REACT_APP_USER_API_URL}/admin/normalize-username/${encodeURIComponent(canonUser.id)}`,
      { method: "POST" }
    );
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (!res.ok) {
      setCanonMsg(json?.detail || "Failed to set canonical username");
      setCanonSaving(false);
      return;
    }

    // Update local list
    setUsers((prev) =>
      prev.map((u) => (u.id === canonUser.id ? { ...u, username_canon: json.username_canon } : u))
    );

    setCanonMsg("Saved ✅");
    setCanonSaving(false);
    // optional close
    closeCanonModal();
  };

  /** ---------- Languages Modal ---------- */

  const openLangEditor = (user) => {
    setLangSaveMsg("");
    setEditLangUser(user);
    setEditL1(typeof user?.l1 === "string" && user.l1.trim() ? user.l1.trim() : null);
    setEditOther(dedupeStringsCaseInsensitive(Array.isArray(user?.otherLanguages) ? user.otherLanguages : []));
  };

  const closeLangEditor = () => {
    setEditLangUser(null);
    setEditL1(null);
    setEditOther([]);
    setLangSaveMsg("");
  };

  const saveLanguages = async () => {
    if (!editLangUser?.username) return;

    setLangSaving(true);
    setLangSaveMsg("");

    const payload = {
      l1: editL1 && editL1.trim() ? editL1.trim() : null,
      otherLanguages: dedupeStringsCaseInsensitive(editOther),
    };

    const res = await authFetch(
      `${process.env.REACT_APP_USER_API_URL}/admin/update-languages/${encodeURIComponent(editLangUser.username)}`,
      { method: "POST", body: JSON.stringify(payload) }
    );
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (!res.ok) {
      setLangSaveMsg(json?.detail || "Failed to update languages");
      setLangSaving(false);
      return;
    }

    // Backend returns { ok, username, l1, otherLanguages }
    setUsers((prev) =>
      prev.map((u) =>
        u.username === editLangUser.username ? { ...u, l1: json.l1, otherLanguages: json.otherLanguages } : u
      )
    );
    setEditLangUser((prev) => (prev ? { ...prev, l1: json.l1, otherLanguages: json.otherLanguages } : prev));
    setLangSaveMsg("Saved ✅");
    setLangSaving(false);
  };

  /** ---------- Rename Modal (optional) ---------- */

  const openRename = (user) => {
    setRenameMsg("");
    setRenameUser(user);
    setRenameValue(user?.username || "");
  };

  const closeRename = () => {
    setRenameUser(null);
    setRenameValue("");
    setRenameMsg("");
    setRenameSaving(false);
  };

  const renameCanonConflict = useMemo(() => {
    const valueCanon = canonicalUsername(renameValue);
    if (!renameUser || !valueCanon) return false;

    const myCanon = getCanonKey(renameUser);
    if (valueCanon === myCanon) return false;

    return (canonCounts.get(valueCanon) || 0) > 0;
  }, [renameValue, renameUser, canonCounts]);

  const saveRename = async () => {
    if (!renameUser?.username) return;

    const newName = stripZeroWidth(renameValue).trim();
    if (!newName) {
      setRenameMsg("Username cannot be empty");
      return;
    }

    setRenameSaving(true);
    setRenameMsg("");

    const res = await authFetch(
      `${process.env.REACT_APP_USER_API_URL}/admin/rename-user/${encodeURIComponent(renameUser.username)}/${encodeURIComponent(
        newName
      )}`,
      { method: "POST" }
    );
    if (!res) return;

    const json = await res.json().catch(() => null);
    if (!res.ok) {
      setRenameMsg(json?.detail || "Rename failed");
      setRenameSaving(false);
      return;
    }

    // Backend returns { ok, id, old, new, username_canon }
    setUsers((prev) =>
      prev.map((u) =>
        u.id === json.id || u.username === renameUser.username
          ? { ...u, username: json.new, username_canon: json.username_canon }
          : u
      )
    );

    setRenameSaving(false);
    closeRename();
    setTimeout(() => updateAccessList(accessPage), 0);
  };

  /** ---------- UI helpers ---------- */

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // ignore
    }
  };

  const searchUsers = async (event) => {
    event.preventDefault();
    setAccessPage(1);
    await updateAccessList(1);
  };

  const searchRequests = async (event) => {
    event.preventDefault();
    setRequestPage(1);
    await updateRequestList(1);
  };

  const handleGoBack = () => {
    if (checkUser) setCheckUser(null);
    else successHandler(false);
  };

  /** ---------- Effects ---------- */

  useEffect(() => {
    updateAccessList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateAccessList(accessPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessPage]);

  useEffect(() => {
    updateRequestList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateRequestList(requestPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestPage]);

  useEffect(() => {
    authFetch(`${process.env.REACT_APP_USER_API_URL}/admin/subscribed`, { method: "GET" })
      ?.then((r) => (r ? r.json().catch(() => null) : null))
      ?.then((json) => {
        if (json) setSubscribed(!!json["subscribed"]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEmailError("");
  }, [email]);

  /** =========================
   *  Render
   *  ========================= */

  return (
    <div className="w-full max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors duration-200 font-medium"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          {checkUser ? "Back to Stats" : "Go back to Main View"}
        </button>

        <div className="text-center flex-1">
          <h1 className="text-3xl font-bold text-blue-700">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage users, roles, and language background</p>
        </div>

        <div className="w-40" />
      </div>

      {!checkUser && (
        <Selector clickHandler={(tab_index) => setActiveTabIndex(tab_index)} tabsData={tabs} active={activeTabIndex} />
      )}

      {/* User Management */}
      {!checkUser && tabs[activeTabIndex] === "User management" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Left */}
            <div className="space-y-5">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="text-xl font-semibold text-gray-900">User Management</h3>
                  <p className="text-sm text-gray-500">
                    Duplicate detection uses backend canonical (username_canon). If missing, we compute it locally.
                  </p>
                </div>

                <form onSubmit={searchUsers} className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={accessSearch}
                    onChange={(e) => setAccessSearch(e.target.value)}
                    className="border border-gray-300 rounded-xl px-3 py-2 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm"
                  >
                    Search
                  </button>
                </form>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-600">Filter role:</span>
                <div className="w-56">
                  <Dropdown
                    options={searchOptions}
                    onChange={(opt) => {
                      setRole(opt);
                      setAccessPage(1);
                      setTimeout(() => updateAccessList(1), 0);
                    }}
                    value={role}
                  />
                </div>
              </div>

              {users.length > 0 ? (
                <div className="space-y-4">
                  <Paginator maxpage={accessPages} page={accessPage} clickHandler={(p) => setAccessPage(p)} />

                  <div className="rounded-2xl border border-gray-200 overflow-hidden">
                    <div className="grid grid-cols-12 gap-4 p-4 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600">
                      <div className="col-span-2">User</div>
                      <div className="col-span-3">Email</div>
                      <div className="col-span-3">Languages</div>
                      <div className="col-span-2">Role</div>
                      <div className="col-span-2 text-center">Actions</div>
                    </div>

                    {users.map((user) => {
                      const dup = isCanonDuplicate(user);
                      const needsSet = needsCanonSet(user);
                      const needsFix = needsCanonFix(user);
                      const canon = getCanonKey(user);

                      return (
                        <div
                          key={`${user.id || ""}:${user.username}`}
                          className="grid grid-cols-12 gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          {/* Username + canonical status */}
                          <div className="col-span-2 min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className={`font-semibold truncate ${
                                  dup ? "text-red-700" : needsSet || needsFix ? "text-amber-700" : "text-gray-900"
                                }`}
                                title={user.username}
                              >
                                {user.username}
                              </div>

                              {dup && (
                                <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 font-semibold">
                                  DUP
                                </span>
                              )}

                              {!dup && (needsSet || needsFix) && (
                                <span className="shrink-0 text-[11px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 font-semibold">
                                  {needsSet ? "NO CANON" : "CANON MISMATCH"}
                                </span>
                              )}
                            </div>

                            <div className="mt-1 text-[11px] text-gray-600 break-words">
                              canon: <span className="font-mono">{canon || "—"}</span>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {/* NEW: set username_canon without renaming */}
                              {(needsSet || needsFix) && user.id && (
                                <button
                                  type="button"
                                  onClick={() => openCanonModal(user)}
                                  className="text-xs font-semibold px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800"
                                  title="Set username_canon for this user (does not change display username)"
                                >
                                  Set canonical
                                </button>
                              )}

                              {/* Optional: keep rename */}
                              <button
                                type="button"
                                onClick={() => openRename(user)}
                                className="text-xs font-semibold text-blue-700 hover:text-blue-900 underline"
                              >
                                Rename
                              </button>
                            </div>
                          </div>

                          {/* Email */}
                          <div className="col-span-3 text-sm text-gray-700">
                            {user.email ? (
                              <div className="flex items-start gap-2">
                                <div className="min-w-0">
                                  <div className="break-words leading-snug" title={user.email}>
                                    {user.email}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => copyToClipboard(user.email)}
                                  className="shrink-0 text-xs font-semibold px-2 py-1 rounded-lg border border-gray-200 hover:bg-white"
                                  title="Copy email"
                                >
                                  Copy
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>

                          {/* Languages */}
                          <div className="col-span-3 text-sm text-gray-700">
                            <div className="leading-snug">
                              <span className="font-semibold">L1:</span>{" "}
                              {user.l1 ? user.l1 : <span className="text-gray-400">—</span>}
                            </div>
                            <div className="leading-snug">
                              <span className="font-semibold">Other:</span>{" "}
                              {user.otherLanguages?.length ? (
                                <span className="break-words">{user.otherLanguages.join(", ")}</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => openLangEditor(user)}
                              className="mt-1 text-xs font-semibold text-blue-700 hover:text-blue-900 underline"
                            >
                              Edit
                            </button>
                          </div>

                          {/* Role */}
                          <div className="col-span-2">
                            <Dropdown
                              options={options}
                              onChange={(option) => addChangeAccess(user.username, user.role, option.label)}
                              value={user.role}
                            />
                          </div>

                          {/* Actions */}
                          <div className="col-span-2 flex items-center justify-center gap-2">
                            <button
                              onClick={() => addDeleteUser(user.username)}
                              className="text-red-700 hover:text-red-800 font-semibold text-sm bg-red-50 hover:bg-red-100 px-3 py-2 rounded-xl border border-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="text-gray-400 text-4xl mb-2">👥</div>
                  <p className="text-gray-500">No users found matching your criteria</p>
                </div>
              )}
            </div>

            {/* Right */}
            <div className="space-y-6">
              {changeList.length > 0 && (
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                  <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">Pending Changes</h4>

                  <div className="space-y-2">
                    {changeList.map((change, i) => (
                      <div key={i} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-100">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{change.username}</div>
                          <div className="text-sm text-gray-600 break-words">{change.action}</div>
                        </div>
                        <button onClick={() => removeChange(i)} className="text-red-600 hover:text-red-700 p-2 rounded-lg">
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={commit}
                    className="w-full mt-3 bg-green-600 text-white py-2 rounded-xl hover:bg-green-700 transition-colors font-semibold"
                  >
                    Commit Changes
                  </button>
                </div>
              )}

              {/* Requests */}
              <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                <h4 className="font-semibold text-blue-900 mb-3">Access Requests</h4>

                <form onSubmit={searchRequests} className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Search requests..."
                    value={requestSearch}
                    onChange={(e) => setRequestSearch(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-3 py-2 rounded-xl hover:bg-blue-700 transition-colors font-semibold text-sm"
                  >
                    Search
                  </button>
                </form>

                {requests.length > 0 ? (
                  <div className="space-y-4">
                    <Paginator maxpage={requestPages} page={requestPage} clickHandler={(p) => setRequestPage(p)} />
                    <div className="space-y-2">
                      {requests.map((request, i) => (
                        <div key={i} className="bg-white p-3 rounded-xl border border-blue-100">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 truncate">{request.username}</div>
                              <div className="text-sm text-gray-600">
                                Requesting: <span className="font-semibold">{request.newRole}</span>
                              </div>
                            </div>

                            {request.newRole.indexOf("/") === -1 && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => addChangeAccess(request.username, request.oldRole, request.newRole)}
                                  className="text-green-700 hover:text-green-800 font-semibold text-sm bg-green-50 hover:bg-green-100 px-3 py-2 rounded-xl border border-green-200 transition-colors"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => addDenyAccess(request.username, request.oldRole, request.newRole)}
                                  className="text-red-700 hover:text-red-800 font-semibold text-sm bg-red-50 hover:bg-red-100 px-3 py-2 rounded-xl border border-red-200 transition-colors"
                                >
                                  Deny
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-600">No pending requests</div>
                )}

                <div className="mt-4 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={subscribed}
                    onChange={handleCheck}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="text-sm text-gray-700">Subscribe to request notifications</label>
                </div>
              </div>

              {/* Invite */}
              <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-4">
                <h4 className="font-semibold text-emerald-900 mb-3">Send Invitation</h4>
                <form onSubmit={sendEmail} className="space-y-3">
                  <input
                    type="email"
                    placeholder="email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                  {emailError && (
                    <div className={`text-sm ${emailError.includes("sent") ? "text-green-700" : "text-red-700"}`}>
                      {emailError}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full bg-emerald-600 text-white py-2 rounded-xl hover:bg-emerald-700 transition-colors font-semibold"
                  >
                    Send Invitation
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {!checkUser && tabs[activeTabIndex] === "Stats" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800">User Statistics</h3>
            <p className="text-gray-600">Select a user to view their learning statistics and progress.</p>

            {users.length > 0 ? (
              <div className="space-y-4">
                <Paginator maxpage={accessPages} page={accessPage} clickHandler={(p) => setAccessPage(p)} />

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {users.map((user) => (
                    <button
                      key={`${user.id || ""}:${user.username}`}
                      onClick={() => setCheckUser(user.username)}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-left group"
                    >
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 group-hover:text-blue-700 truncate">{user.username}</div>
                        <div className="text-sm text-gray-500 capitalize">{user.role?.toLowerCase?.() || user.role}</div>
                        <div className="text-xs text-gray-500 break-words">{user.email || "—"}</div>
                        <div className="text-xs text-gray-500">
                          L1: {user.l1 || "—"} · Other: {user.otherLanguages?.length ? user.otherLanguages.join(", ") : "—"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <div className="text-gray-400 text-4xl mb-2">📊</div>
                <p className="text-gray-500">No users available for statistics</p>
              </div>
            )}
          </div>
        </div>
      )}

      {checkUser && (
        <div className="w-full mt-6">
          <CheckUserStats checkuser={checkUser} successHandler={setCheckUser} />
        </div>
      )}

      {/* Languages Modal */}
      {editLangUser && (
        <div
          className="fixed inset-0 z-[999999] bg-black/40 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeLangEditor();
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-gray-900">Edit language background</h3>
                <p className="text-xs text-gray-600 break-words">
                  {editLangUser.username} · {editLangUser.email || "no email"}
                </p>
              </div>
              <button
                type="button"
                onClick={closeLangEditor}
                className="h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-50 text-gray-600"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-gray-800">
                  This helps tailor exercises (sounds, minimal pairs, and patterns) to the learner’s background.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Mother tongue (L1)</label>
                <Select
                  isClearable
                  options={languageOptions}
                  components={{ Option: LanguageOption, SingleValue: LanguageSingleValue }}
                  value={editL1 ? makeOptionFromValue(editL1) : null}
                  onChange={(opt) => setEditL1(opt ? opt.value : null)}
                  placeholder="Select L1..."
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />

                <p className="text-[11px] text-gray-500 mt-1">If not listed, type it here:</p>
                <input
                  value={
                    typeof editL1 === "string" && editL1 && !languageOptions.some((o) => o.value === editL1) ? editL1 : ""
                  }
                  onChange={(e) => setEditL1(e.target.value)}
                  placeholder="Custom L1 (optional)"
                  className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Other languages</label>
                <Select
                  isMulti
                  isClearable
                  options={languageOptions}
                  components={{ Option: LanguageOption, MultiValueLabel: LanguageMultiValueLabel }}
                  value={(editOther || []).map((v) => makeOptionFromValue(v))}
                  onChange={(opts) => setEditOther(dedupeStringsCaseInsensitive((opts || []).map((o) => o.value)))}
                  placeholder="Select other languages..."
                  classNamePrefix="react-select"
                  styles={selectStyles}
                />

                <p className="text-[11px] text-gray-500 mt-1">Add manually:</p>
                <ManualAddOther values={editOther} onChange={(next) => setEditOther(dedupeStringsCaseInsensitive(next))} />
              </div>

              {langSaveMsg && (
                <div className={`text-sm ${langSaveMsg.includes("Saved") ? "text-green-700" : "text-red-700"}`}>
                  {langSaveMsg}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeLangEditor}
                className="px-3 py-2 rounded-xl text-sm border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveLanguages}
                disabled={langSaving}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                {langSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Canonical Modal (NEW) */}
      {canonUser && (
        <div
          className="fixed inset-0 z-[999999] bg-black/40 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeCanonModal();
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-gray-900">Set canonical username</h3>
                <p className="text-xs text-gray-600 break-words">
                  Display name stays the same. We only set <span className="font-mono">username_canon</span>.
                </p>
              </div>
              <button
                type="button"
                onClick={closeCanonModal}
                className="h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-50 text-gray-600"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-800">
                  <div>
                    User: <span className="font-semibold">{canonUser.username}</span>
                  </div>
                  <div className="mt-1">
                    Computed canonical:{" "}
                    <span className="font-mono">{canonicalUsername(canonUser.username)}</span>
                  </div>
                </div>
              </div>

              {isCanonDuplicate(canonUser) && (
                <div className="text-sm text-red-700">
                  Warning: This canonical value is already duplicated. Saving may fail with 409.
                </div>
              )}

              {canonMsg && <div className={`text-sm ${canonMsg.includes("✅") ? "text-green-700" : "text-red-700"}`}>{canonMsg}</div>}
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCanonModal}
                className="px-3 py-2 rounded-xl text-sm border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveCanonical}
                disabled={canonSaving}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                {canonSaving ? "Saving..." : "Save canonical"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal (optional) */}
      {renameUser && (
        <div
          className="fixed inset-0 z-[999999] bg-black/40 flex items-center justify-center p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeRename();
          }}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-gray-200">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-gray-900">Rename user (optional)</h3>
                <p className="text-xs text-gray-600 break-words">
                  Current: <span className="font-semibold">{renameUser.username}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={closeRename}
                className="h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-50 text-gray-600"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">New username</label>
                <input
                  value={renameValue}
                  onChange={(e) => {
                    setRenameMsg("");
                    setRenameValue(e.target.value);
                  }}
                  className={`w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 ${
                    renameCanonConflict ? "border-red-300 focus:ring-red-200" : "border-gray-300 focus:ring-blue-200"
                  }`}
                  placeholder="Enter new username..."
                />
                <div className="mt-1 text-[11px] text-gray-600">
                  New canonical: <span className="font-mono">{canonicalUsername(renameValue)}</span>
                </div>

                {renameCanonConflict && (
                  <div className="mt-2 text-sm text-red-700">
                    Conflicts with an existing user (by canonical).
                  </div>
                )}
              </div>

              {renameMsg && (
                <div className={`text-sm ${renameMsg.includes("✅") ? "text-green-700" : "text-red-700"}`}>{renameMsg}</div>
              )}
            </div>

            <div className="p-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeRename}
                className="px-3 py-2 rounded-xl text-sm border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRename}
                disabled={renameSaving || renameCanonConflict}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                {renameSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Manual add helper for “other languages” */
function ManualAddOther({ values, onChange }) {
  const [text, setText] = useState("");

  const add = () => {
    const v = cleanText(text);
    if (!v) return;
    onChange([...(values || []), v]);
    setText("");
  };

  return (
    <div className="mt-2">
      <div className="flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="Type a language and press Enter"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700"
        >
          Add
        </button>
      </div>

      {(values || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {values.map((v) => (
            <span key={v} className="text-xs bg-gray-50 border border-gray-200 rounded-full px-2 py-1">
              {v}
              <button
                type="button"
                onClick={() => onChange((values || []).filter((x) => x !== v))}
                className="ml-2 text-gray-500 hover:text-gray-800"
                aria-label={`Remove ${v}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default AccessInterface;